/**
 * The gate the whole app sits behind.
 *
 * It is a *display* decision, never a security one — the server rejects an
 * unknown device on every single request regardless of what is rendered here.
 * What this gate buys is that a stranger who finds the address sees a closed
 * door instead of the family's names, and that the children see the game
 * instantly whether or not there is any signal.
 *
 * The order of the branches below is the whole design:
 *   allowed      → the app, immediately, network or not
 *   unknown      → a first run, which is the only case that may wait
 *   unreachable  → a first run with no server: say so, do not accuse
 *   locked       → the server said no
 */

import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/store/auth';

/** Reachable without a grant: redeeming an invite, and the admin login. */
const OPEN_PATHS = ['/uitnodiging', '/beheer/login'];

export default function AuthGate({ children }: { children: ReactNode }) {
  const status = useAuth((state) => state.status);
  const refresh = useAuth((state) => state.refresh);
  const { pathname } = useLocation();

  useEffect(() => {
    // Fire and forget. Nothing below waits for it; it only ever *corrects* what
    // the hint already decided.
    void refresh();
  }, [refresh]);

  // Re-check when the app comes back to the foreground, which on a phone is the
  // moment a revocation or a fresh invite should take effect.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [refresh]);

  if (OPEN_PATHS.some((path) => pathname.startsWith(path))) return <>{children}</>;
  if (status === 'allowed') return <>{children}</>;
  if (status === 'unknown') return <Waiting />;
  return <Closed offline={status === 'unreachable'} />;
}

function Waiting() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-slate-500">
      <span className="animate-pulse text-sm">Even kijken…</span>
    </div>
  );
}

function Closed({ offline }: { offline: boolean }) {
  const retry = useAuth((state) => state.refresh);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-6 text-center text-white">
      <div className="text-6xl" aria-hidden>
        🂡
      </div>

      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Boerenbridge</h1>
        {offline ? (
          <p className="max-w-xs text-slate-400">
            Dit toestel kan de server niet bereiken. Verbinding maken en het opnieuw proberen.
          </p>
        ) : (
          <p className="max-w-xs text-slate-400">
            Dit is een privé-tafel. Vraag om een uitnodiging — dan scan je de QR-code één keer en
            hoef je daarna nooit meer in te loggen.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => void retry()}
        className="min-h-12 rounded-xl bg-base-700 px-5 text-base hover:bg-base-600"
      >
        Opnieuw proberen
      </button>

      {/*
        A plain link, not a router link with an eye-catching style: the children
        never need this, and it should not look like the way in for a stranger.
      */}
      <a href="/beheer/login" className="text-sm text-slate-600 underline underline-offset-4">
        Beheer
      </a>
    </div>
  );
}
